import * as React from "react";
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	Stack,
	Tab,
	Tabs,
	TextField,
	Typography,
} from "@mui/material";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";

import {
	REASON_LABELS,
	SuppressionReason,
	useBulkCreateSuppressions,
	useCreateSuppression,
} from "@/hooks/api/use-suppressions";

const REASONS = Object.keys(REASON_LABELS) as SuppressionReason[];

/** Splits a pasted list on newlines, commas, semicolons or tabs. */
function parseNumbers(raw: string): string[] {
	return raw
		.split(/[\n,;\t]+/)
		.map((value) => value.trim())
		.filter(Boolean);
}

export function AddToDoNotCallDialog({
	open,
	onClose,
}: {
	open: boolean;
	onClose: () => void;
}): React.JSX.Element {
	const [tab, setTab] = React.useState(0);
	const [phone, setPhone] = React.useState("");
	const [bulk, setBulk] = React.useState("");
	const [reason, setReason] = React.useState<SuppressionReason>("do_not_call");
	const [note, setNote] = React.useState("");

	const createMutation = useCreateSuppression();
	const bulkMutation = useBulkCreateSuppressions();
	const isPending = createMutation.isPending || bulkMutation.isPending;

	const parsed = React.useMemo(() => parseNumbers(bulk), [bulk]);

	const reset = () => {
		setPhone("");
		setBulk("");
		setNote("");
		setReason("do_not_call");
		setTab(0);
	};

	const handleClose = () => {
		if (isPending) return;
		reset();
		onClose();
	};

	const handleSubmit = async () => {
		if (tab === 0) {
			if (!phone.trim()) return;
			await createMutation.mutateAsync({ phone: phone.trim(), reason, note: note.trim() || undefined });
		} else {
			if (parsed.length === 0) return;
			await bulkMutation.mutateAsync({ numbers: parsed, reason, note: note.trim() || undefined });
		}
		reset();
		onClose();
	};

	const canSubmit = tab === 0 ? Boolean(phone.trim()) : parsed.length > 0;

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			sx={{
				"& .MuiDialog-container": {
					"& .MuiPaper-root": {
						width: "100%",
					},
				},
			}}
		>
			<DialogTitle>
				Add to do-not-call list
				<IconButton color="inherit" onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
					<XIcon width={20} height={20} />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ py: 4!, pt: "8px !important" }}>
				<Stack spacing={2.5}>
					<Alert severity="warning">
						This applies to <strong>every campaign in this account</strong>, now and in future. Any leads already
						waiting for these numbers are cancelled immediately.
					</Alert>

					<Tabs value={tab} onChange={(_, value: number) => setTab(value)}>
						<Tab label="One number" />
						<Tab label="Paste a list" />
					</Tabs>

					{tab === 0 ? (
						<TextField
							label="Phone number"
							value={phone}
							onChange={(event) => setPhone(event.target.value)}
							placeholder="+60 12 345 6789"
							helperText="Any format. Include the country code where you can - without it we cannot always tell which country a number belongs to."
							fullWidth
							autoFocus
						/>
					) : (
						<Stack spacing={1}>
							<TextField
								label="Phone numbers"
								value={bulk}
								onChange={(event) => setBulk(event.target.value)}
								placeholder={"+60123456789\n+60198765432"}
								multiline
								minRows={6}
								maxRows={14}
								fullWidth
								autoFocus
							/>
							<Typography variant="caption" color="text.secondary">
								One per line, or separated by commas. {parsed.length > 0 ? `${parsed.length} number(s) found.` : ""}
							</Typography>
						</Stack>
					)}

					<TextField select label="Reason" value={reason} onChange={(event) => setReason(event.target.value as SuppressionReason)} fullWidth>
						{REASONS.map((value) => (
							<MenuItem key={value} value={value}>
								{REASON_LABELS[value]}
							</MenuItem>
						))}
					</TextField>

					<TextField
						label="Note (optional)"
						value={note}
						onChange={(event) => setNote(event.target.value)}
						placeholder="Where this came from, or what the person said"
						helperText="Kept as evidence. Worth filling in if you are ever asked to show why a number was suppressed."
						multiline
						minRows={2}
						fullWidth
					/>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ borderTop: "1px solid var(--mui-palette-divider)", py: 2, px: 3 }}>
				<Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
					<Button color="secondary" onClick={handleClose} disabled={isPending}>
						Cancel
					</Button>
					<Button color="primary" variant="contained" onClick={handleSubmit} disabled={!canSubmit || isPending}>
						{tab === 0 ? "Add" : `Add ${parsed.length > 0 ? parsed.length : ""} number(s)`}
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
}
